import React, { ReactElement, useState } from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import Image from 'next/image';
import { StaticImageData } from 'next/image';
import Slider from 'react-slick';
import styled from 'styled-components';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import { useCardCount } from 'static_pages/home/hooks/useCardCount';

type StringInputFunction = (input: string) => void;

interface Cta {
  title: string;
  variant: 'secondary' | 'primary'; // You can add more variants if needed
  link: string;
  onClick: StringInputFunction;
}

interface Card {
  title: string;
  description: string;
  image: StaticImageData | string; // If image is React component (SVG) or a URL string
  chips: string[]; // An array of strings
  subtitle: ReactElement;
  moreLink: string;
  moreLinkText: string;
  cta: Cta;
}

export type CardsArray = Card[];

export function CardsSlider({ cards = [] }: { cards: CardsArray }) {
  const mobile = useBreakpointDown('sm');
  const [currentTab, setCurrentTab] = useState(0);
  const cardCount = useCardCount();
  const slidesToShow = cardCount;

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    centerMode: false,
    beforeChange: (oldIndex: number, newIndex: number) =>
      setCurrentTab(Math.round(newIndex)),
    arrows: !mobile,
    prevArrow: <BackIcon />,
    nextArrow: <NextIcon />,
    swipeToSlide: true,
    draggable: true,
    appendDots: (dots: [ReactElement]) => (
      <div style={{ bottom: '-40px' }}>
        <ul style={{ margin: '0px', padding: 0 }}> {dots} </ul>
      </div>
    ),
    customPaging: (i: number) => (
      <div className={`custom-dot currentTab === i ? 'active' : ''}`}>
        <IndicatorIcon
          style={{
            fill: currentTab === i ? '#3C4340' : '#E3E4E4',
          }}
        />
      </div>
    ),
  };

  return (
    <>
      <SliderWrapper>
        <Slider {...settings}>
          {cards.map((card, index) => (
            <Card key={index}>
              <Container>
                <div>
                  <Image
                    priority
                    src={card.image}
                    height={197}
                    width={300}
                    quality={100}
                    alt={card.title}
                  />
                  <Title>{card.title}</Title>
                  <Description>{card.description}</Description>
                  <ChipsWrapper>
                    {card.chips.map((chip, index) => (
                      <Chip key={`chip-${index}`}>{chip}</Chip>
                    ))}
                  </ChipsWrapper>
                  <Subtitle>{card.subtitle}</Subtitle>
                  <Box my={2}>
                    <MoreLink href={card.moreLink} target="_blank">
                      {card.moreLinkText}
                    </MoreLink>
                  </Box>
                </div>
                <div>
                  <StyledButton
                    onClick={() => card.cta.onClick(card.cta.link)}
                    variant={card.cta.variant}
                  >
                    {card.cta.title}
                  </StyledButton>
                </div>
              </Container>
            </Card>
          ))}
        </Slider>
      </SliderWrapper>
    </>
  );
}

const Title = styled(Typography)`
  font-size: 23px;
  font-weight: 700;
  line-height: 34.5px;
  margin-top: ${({ theme }) => theme.spacing(3)}px;
`;

const Description = styled(Typography)`
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.4px;
  margin-top: ${({ theme }) => theme.spacing(1)}px;
`;

const Subtitle = styled(Typography)`
  font-size: 12px;
  line-height: 20.16px;
  letter-spacing: 0.4px;
`;

const MoreLink = styled.a`
  color: #3C4340 !important;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 17px
  letter-spacing: 0.4px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

const StyledButton = styled(Button)`
  border-radius: 47px;
  padding: ${({ theme }) => theme.spacing(1, 3)};
  width: fill-available;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const ChipsWrapper = styled(Box)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)}px;
  flex-wrap: wrap;
  margin: ${({ theme }) => theme.spacing(2, 0)};
`;

const Chip = styled(Box)`
  background: #ffffff;
  border-radius: 33px;
  border: 1px solid #e3e4e4;
  border-radius: 33px;
  padding: ${({ theme }) => theme.spacing(1)}px;
  white-space: nowrap;
  font-size: 10px;
  line-height: 17px;
  letter-spacing: 0.4px;
`;

const IndicatorIcon = styled(FiberManualRecordIcon)`
  font-size: 11px;
  margin: 0 4px;
  &:hover {
    fill: #3c4340;
  }
`;

const Card = styled(Box)`
  display: block !important;
  margin: 24px 12px;
  ${(props) => props.theme.breakpoints.down('sm')} {
    margin: 24px auto;
  }
  width: 348px !important;
  height: 650px !important;
  padding: ${({ theme }) => theme.spacing(3)}px;
  gap: 32px;
  border-radius: 20px;
  background: #ffffff;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 4px 40px 0 rgba(62, 41, 29, 0.08);
`;

const SliderWrapper = styled(Box)`
  padding: 0;
`;

const BackIcon = styled(ArrowBackIosIcon)`
  font-size: 46px !important;
  height: 46px !important;
  width: 46px !important;
  color: #e3e4e4 !important;
  margin-left: -20px;
  z-index: 1;
  &:hover {
    color: #3c4340;
  }
`;

const NextIcon = styled(ArrowBackIosIcon)`
  font-size: 46px !important;
  height: 46px !important;
  width: 46px !important;
  transform: translateY(-50%) rotate(180deg) !important;
  color: #3c4340 !important;
  margin-right: -20px;
  z-index: 1;
  &:hover {
    color: #3c4340 !important;
  }
`;
